import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { useToast } from '@kanda-libs/ks-design-library';
import {
  FirebaseAuthService,
  actions,
  useCurrentUser,
  useMutate,
  useSignIn,
} from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/shared/App';
import { URLS } from 'components/shared/App/constants';
import useApiError from 'hooks/useApiError';
import { selectors } from 'store';

import { getError, isStaff } from './helpers';

export interface LoginHook {
  signInWithGoogle: () => void;
  disabled: boolean;
  isSubmiting: boolean;
}

function useLogin(): LoginHook {
  const { push } = useHistory();

  const dispatch = useAppDispatch();
  const meIsSubmitting = useSelector(selectors.authUser.getIsSubmitting);
  const { user, isUserLoggedIn, revalidate, isValidating } = useCurrentUser();

  const { mutate: googleSignIn, isLoading: signInIsSubmitting } =
    useSignIn('googlePopup');

  const { mutate: logout } = useMutate(FirebaseAuthService.logout);

  const { showError } = useToast();
  const onError = useApiError('Error accessing user data');

  const afterSignIn = useCallback(() => {
    revalidate().then(() => {
      dispatch(
        actions.me({
          onError,
          onSuccess: (data) => {
            const { email } = data;
            const staff = isStaff(email);
            if (!staff) {
              showError('Not Kanda/Ideal staff - logged out');
              logout(false);
              return;
            }
            push(URLS.home);
          },
        }),
      );
    });
  }, [dispatch, revalidate, push, onError]);

  const signInWithGoogle = useCallback(() => {
    googleSignIn(false).then(({ error: signInGoogleError }) => {
      if (signInGoogleError) {
        showError(getError(signInGoogleError));
        return;
      }
      afterSignIn();
    });
  }, [googleSignIn, afterSignIn, showError]);

  const isSubmiting = useMemo(
    () => Boolean(signInIsSubmitting || isValidating || meIsSubmitting),
    [],
  );

  const disabled = isSubmiting || false;

  const initRef = useRef<boolean>(false);
  useEffect(() => {
    if (isValidating || initRef.current) return;
    initRef.current = true;
    if (!isUserLoggedIn) return;
    const email = user?.email;
    if (!email) return;
    const staff = isStaff(email);
    if (!staff) {
      logout(false);
      return;
    }
    push(URLS.home);
  }, [user, isValidating, isUserLoggedIn, logout]);

  return {
    signInWithGoogle,
    disabled,
    isSubmiting,
  };
}

export default useLogin;
